const handleResponse = async (response) => {
    if (response.status === 401) {
        const url = new URL(response.url);
        if (url.pathname !== '/api/v1/login') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
        }
    }

    if (response.status === 204) {
        return { success: true };
    }

    const data = await response.json();
    if (!data.success) {
        if (data.error_code) {
            throw new Error(data.error_code);
        }
        throw new Error(data.message || 'An error occurred.');
    }
    return data;
};

export const fetchProfile = async () => {
    const response = await fetch('/api/v1/profile', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const updateProfile = async (profileData) => {
    const response = await fetch('/api/v1/profile', {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profileData)
    });
    return handleResponse(response);
};

export const fetchSexes = async () => {
    const response = await fetch('/api/v1/sexes');
    return handleResponse(response);
};

export const login = async (email, password) => {
    const response = await fetch('/api/v1/login', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
    });
    return handleResponse(response);
};

export const fetchUserSchools = async () => {
    const response = await fetch('/api/v1/schools', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const selectSchool = async (email, password, schoolId) => {
    const response = await fetch('/api/v1/login/select-school', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, school_id: schoolId }),
    });
    return handleResponse(response);
};

export const createSchool = async (name, sevpCode, state) => {
    const response = await fetch('/api/v1/schools', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name, sevp_code: sevpCode, state: state })
    });
    return handleResponse(response);
};

export const selectSchoolAcceptInvite = async (email, password, inviteId) => {
    const response = await fetch('/api/v1/login/select-school/accept', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, invite_id: inviteId }),
    });
    return handleResponse(response);
};

export const fetchStaff = async () => {
    const response = await fetch('/api/v1/staff', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchStaffById = async (staffId) => {
    const response = await fetch(`/api/v1/staff/${staffId}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createStaff = async (staffData) => {
    const response = await fetch('/api/v1/staff', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(staffData)
    });
    return handleResponse(response);
};

export const updateStaff = async (staffId, staffData) => {
    const response = await fetch(`/api/v1/staff/${staffId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(staffData)
    });
    return handleResponse(response);
};

export const deleteStaff = async (staffId) => {
    const response = await fetch(`/api/v1/staff/${staffId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const inviteStaff = async (email, roles) => {
    const response = await fetch('/api/v1/staff/invite', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email, roles })
    });
    return handleResponse(response);
};

export const fetchRolesAPI = async () => {
    const response = await fetch('/api/v1/roles', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    const data = await handleResponse(response);
    // Transform the roles object into an array of objects
    const rolesArray = Object.keys(data.data).map(key => ({
        id: parseInt(key, 10),
        name: data.data[key]
    }));
    return { ...data, data: rolesArray };
};

export const fetchStudents = async (filters) => {
    const queryParams = new URLSearchParams();
    if (filters.type) {
        queryParams.append('type', filters.type);
    }
    if (filters.main_status) {
        queryParams.append('main_status', filters.main_status);
    }
    if (filters.inner_status) {
        queryParams.append('inner_status', filters.inner_status);
    }
    if (filters.page) {
        queryParams.append('page', filters.page);
    }
    if (filters.limit) {
        queryParams.append('limit', filters.limit);
    }
    const response = await fetch(`/api/v1/students?${queryParams.toString()}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchStudentById = async (studentId) => {
    const response = await fetch(`/api/v1/students/${studentId}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createStudent = async (studentData) => {
    const response = await fetch('/api/v1/students', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(studentData)
    });
    return handleResponse(response);
};

export const updateStudent = async (studentId, studentData) => {
    const response = await fetch(`/api/v1/students/${studentId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(studentData)
    });
    return handleResponse(response);
};

export const deleteStudent = async (studentId) => {
    const response = await fetch(`/api/v1/students/${studentId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const changeStudentStatus = async (studentId, mainStatus, innerStatus) => {
    const response = await fetch(`/api/v1/students/${studentId}/status`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
            main_status: parseInt(mainStatus, 10),
            inner_status: parseInt(innerStatus, 10)
        })
    });
    return handleResponse(response);
};

export const fetchStudentTypes = async () => {
    const response = await fetch('/api/v1/student-types');
    return handleResponse(response);
};

export const fetchStudentStatuses = async () => {
    const response = await fetch('/api/v1/student-statuses');
    return handleResponse(response);
};

export const fetchStudentFileTypes = async () => {
    const response = await fetch('/api/v1/student-file-types');
    return handleResponse(response);
};

export const fetchCountries = async () => {
    const response = await fetch('/api/v1/countries');
    return handleResponse(response);
};

export const fetchStates = async () => {
    const response = await fetch('/api/v1/states');
    return handleResponse(response);
};

export const deleteStudentAddressUS = async (studentId) => {
    const response = await fetch(`/api/v1/students/${studentId}/address-us`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const deleteStudentAddressHC = async (studentId) => {
    const response = await fetch(`/api/v1/students/${studentId}/address-hc`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const acceptInvite = async (inviteId) => {
    const response = await fetch(`/api/v1/invite/${inviteId}/accept`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const rejectInvite = async (inviteId) => {
    const response = await fetch(`/api/v1/invite/${inviteId}/reject`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const recallInvite = async (inviteId) => {
    const response = await fetch(`/api/v1/invite/${inviteId}/recall`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchDashboard = async () => {
    const response = await fetch('/api/v1/dashboard', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchNews = async () => {
    const response = await fetch('/api/v1/news', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createNews = async (newsData) => {
    const response = await fetch('/api/v1/news', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newsData)
    });
    return handleResponse(response);
};

export const updateNews = async (newsId, newsData) => {
    const response = await fetch(`/api/v1/news/${newsId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newsData)
    });
    return handleResponse(response);
};

export const deleteNews = async (newsId) => {
    const response = await fetch(`/api/v1/news/${newsId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchPolicies = async () => {
    const response = await fetch('/api/v1/policies', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const updatePolicy = async (policyId, policyData) => {
    const response = await fetch(`/api/v1/policies/${policyId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(policyData)
    });
    return handleResponse(response);
};

export const deletePolicy = async (policyId) => {
    const response = await fetch(`/api/v1/policies/${policyId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};
export const fetchPolicyById = async (policyId) => {
    const response = await fetch(`/api/v1/policies/${policyId}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};
export const createPolicy = async (policyData) => {
    const response = await fetch('/api/v1/policies', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(policyData)
    });
    return handleResponse(response);
};

export const createAgent = async (agentData) => {
    const response = await fetch('/api/v1/agents', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(agentData)
    });
    return handleResponse(response);
};
export const updateAgent = async (agentId, agentData) => {
    const response = await fetch(`/api/v1/agents/${agentId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(agentData)
    });
    return handleResponse(response);
};
export const deleteAgent = async (agentId) => {
    const response = await fetch(`/api/v1/agents/${agentId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};


export const fetchAgents = async () => {
    const response = await fetch('/api/v1/agents', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};
export const fetchAgentById = async (agentId) => {
    const response = await fetch(`/api/v1/agents/${agentId}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchStudentNotes = async (studentId) => {
    const response = await fetch(`/api/v1/students/${studentId}/notes`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};
export const createStudentNote = async (studentId, noteData) => {
    const response = await fetch(`/api/v1/students/${studentId}/notes`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(noteData)
    });
    return handleResponse(response);
};
export const updateNote = async (noteId, noteData) => {
    const response = await fetch(`/api/v1/students/notes/${noteId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(noteData)
    });
    return handleResponse(response);
};

export const deleteNote = async (noteId) => {
    const response = await fetch(`/api/v1/students/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const downloadStudentFile = async (fileId) => {
    const response = await fetch(`/api/v1/files/student-files/${fileId}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            if (errorData.error_code) {
                throw new Error(errorData.error_code);
            }
            throw new Error(errorData.message || 'An error occurred.');
        } catch (e) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'downloaded-file';
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1];
        }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const deleteStudentFile = async (fileId) => {
    const response = await fetch(`/api/v1/files/student-files/${fileId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const uploadStudentFile = async (studentId, formData) => {
    const response = await fetch(`/api/v1/files/students/${studentId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
    });
    return handleResponse(response);
};

export const fetchAgentFileTypes = async () => {
    const response = await fetch('/api/v1/agent-file-types');
    return handleResponse(response);
};

export const uploadAgentFile = async (agentId, formData) => {
    const response = await fetch(`/api/v1/files/agents/${agentId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
    });
    return handleResponse(response);
};

export const downloadAgentFile = async (fileId) => {
    const response = await fetch(`/api/v1/files/agent-files/${fileId}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            if (errorData.error_code) {
                throw new Error(errorData.error_code);
            }
            throw new Error(errorData.message || 'An error occurred.');
        } catch (e) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'downloaded-file';
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1];
        }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const deleteAgentFile = async (fileId) => {
    const response = await fetch(`/api/v1/files/agent-files/${fileId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const downloadI20File = async (fileId) => {
    const response = await fetch(`/api/v1/files/i20-files/${fileId}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            if (errorData.error_code) {
                throw new Error(errorData.error_code);
            }
            throw new Error(errorData.message || 'An error occurred.');
        } catch (e) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'downloaded-file';
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1];
        }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const deleteI20 = async (studentId) => {
    const response = await fetch(`/api/v1/students/${studentId}/i20`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const fetchStreams = async () => {
    const response = await fetch('/api/v1/streams', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchTerms = async (showArchived = false) => {
    const response = await fetch(`/api/v1/terms?show_archived=${showArchived}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createTerm = async (termData) => {
    const response = await fetch('/api/v1/terms', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(termData)
    });
    return handleResponse(response);
};

export const updateTerm = async (termId, termData) => {
    const response = await fetch(`/api/v1/terms/${termId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(termData)
    });
    return handleResponse(response);
};

export const deleteTerm = async (termId) => {
    const response = await fetch(`/api/v1/terms/${termId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const archiveTerm = async (termId) => {
    const response = await fetch(`/api/v1/terms/${termId}/archive`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchSubjects = async () => {
    const response = await fetch('/api/v1/subjects', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchLevels = async () => {
    const response = await fetch('/api/v1/levels', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createSubject = async (subjectData) => {
    const response = await fetch('/api/v1/subjects', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(subjectData)
    });
    return handleResponse(response);
};

export const updateSubject = async (subjectId, subjectData) => {
    const response = await fetch(`/api/v1/subjects/${subjectId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(subjectData)
    });
    return handleResponse(response);
};

export const deleteSubject = async (subjectId) => {
    const response = await fetch(`/api/v1/subjects/${subjectId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const updateLevel = async (levelId, levelData) => {
    const response = await fetch(`/api/v1/levels/${levelId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(levelData)
    });
    return handleResponse(response);
};

export const deleteLevel = async (levelId) => {
    const response = await fetch(`/api/v1/levels/${levelId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const updateStream = async (streamId, streamData) => {
    const response = await fetch(`/api/v1/streams/${streamId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(streamData)
    });
    return handleResponse(response);
};

export const createLevel = async (levelData) => {
    const response = await fetch('/api/v1/levels', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(levelData)
    });
    return handleResponse(response);
};

export const createStream = async (streamData) => {
    const response = await fetch('/api/v1/streams', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(streamData)
    });
    return handleResponse(response);
};

export const deleteStream = async (streamId) => {
    const response = await fetch(`/api/v1/streams/${streamId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const getGroupsByTerm = async (showArchived = false) => {
    const response = await fetch(`/api/v1/groups/by-term?show_archived=${showArchived}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createTlsg = async (tlsgData) => {
    const response = await fetch('/api/v1/tlsgs', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(tlsgData)
    });
    return handleResponse(response);
};

export const createAllForTerm = async (termId) => {
    const response = await fetch(`/api/v1/tlsgs/terms/${termId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createI20 = async (studentId, formData) => {
    const response = await fetch(`/api/v1/students/${studentId}/i20`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
    });
    return handleResponse(response);
};

export const getSevisStatuses = async () => {
    const response = await fetch('/api/v1/sevis-statuses', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createClassesAndGroups = async (termId) => {
    const response = await fetch(`/api/v1/classes/terms/${termId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createClass = async (classData) => {
    const response = await fetch('/api/v1/classes', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(classData)
    });
    return handleResponse(response);
};

export const deleteClass = async (classId) => {
    const response = await fetch(`/api/v1/classes/${classId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createGroup = async (groupData) => {
    const response = await fetch('/api/v1/groups', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(groupData)
    });
    return handleResponse(response);
};

export const deleteGroup = async (groupId) => {
    const response = await fetch(`/api/v1/groups/${groupId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const getClasses = async () => {
    const response = await fetch('/api/v1/classes', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};


export const getGroups = async () => {
    const response = await fetch('/api/v1/groups', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const assignStudentToGroup = async (studentId, groupId) => {
    const response = await fetch(`/api/v1/students/${studentId}/groups`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ group_id: groupId })
        });
    return handleResponse(response);
};

export const fetchStudentGroups = async (studentId, showArchived = false) => {
    const response = await fetch(`/api/v1/students/${studentId}/groups?show_archived=${showArchived}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const getRoster = async () => {
    const response = await fetch('/api/v1/roster', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchFees = async () => {
    const response = await fetch('/api/v1/fees', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createFee = async (feeData) => {
    const response = await fetch('/api/v1/fees', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(feeData)
    });
    return handleResponse(response);
};

export const fetchDiscounts = async () => {
    const response = await fetch('/api/v1/discounts', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createDiscount = async (discountData) => {
    const response = await fetch('/api/v1/discounts', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(discountData)
    });
    return handleResponse(response);
};

export const editFee = async (feeData) => {
    const response = await fetch(`/api/v1/fees/${feeData.id}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(feeData)
    });
    return handleResponse(response);
};

export const editDiscount = async (discountData) => {
    const response = await fetch(`/api/v1/discounts/${discountData.id}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(discountData)
    });
    return handleResponse(response);
};

export const deleteFee = async (feeId) => {
    const response = await fetch(`/api/v1/fees/${feeId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const deleteDiscount = async (discountId) => {
    const response = await fetch(`/api/v1/discounts/${discountId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const fetchRecurringInvoices = async (sortBy, sortDir, page, limit) => {
    const queryParams = new URLSearchParams();
    if (sortBy) {
        queryParams.append('sort_by', sortBy);
    }
    if (sortDir) {
        queryParams.append('sort_dir', sortDir);
    }
    if (page) {
        queryParams.append('page', page);
    }
    if (limit) {
        queryParams.append('limit', limit);
    }
    const response = await fetch(`/api/v1/recurring-invoices?${queryParams.toString()}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchRecurringInvoiceIntervals = async () => {
    const response = await fetch('/api/v1/recurring-invoice-intervals', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createRecurringInvoice = async (studentId, invoiceData) => {
    const response = await fetch(`/api/v1/students/${studentId}/recurring-invoices`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(invoiceData)
    });
    return handleResponse(response);
};

export const fetchRecurringInvoiceById = async (id) => {
    const response = await fetch(`/api/v1/recurring-invoices/${id}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const deleteRecurringInvoice = async (id) => {
    const response = await fetch(`/api/v1/recurring-invoices/${id}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const fetchInvoices = async (sortBy, sortDir, page, limit, from, to) => {
    const queryParams = new URLSearchParams();
    if (sortBy) {
        queryParams.append('sort_by', sortBy);
    }
    if (sortDir) {
        queryParams.append('sort_dir', sortDir);
    }
    if (page) {
        queryParams.append('page', page);
    }
    if (limit) {
        queryParams.append('limit', limit);
    }
    if (from) {
        queryParams.append('from', from);
    }
    if (to) {
        queryParams.append('to', to);
    }
    const response = await fetch(`/api/v1/invoices?${queryParams.toString()}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchInvoiceById = async (id) => {
    const response = await fetch(`/api/v1/invoices/${id}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const deleteInvoice = async (invoiceId) => {
    const response = await fetch(`/api/v1/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const createInvoice = async (studentId, invoiceData) => {
    const response = await fetch(`/api/v1/students/${studentId}/invoices`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(invoiceData)
    });
    return handleResponse(response);
};

export const sendInvoice = async (invoiceId) => {
    const response = await fetch(`/api/v1/invoices/${invoiceId}/send`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const recordPayment = async (invoiceId, paymentData) => {
    const response = await fetch(`/api/v1/invoices/${invoiceId}/payment`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(paymentData)
    });
    return handleResponse(response);
};

export const deletePayment = async (paymentId) => {
    const response = await fetch(`/api/v1/payments/${paymentId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const fetchPayments = async (sortBy, sortDir, page, limit, from, to) => {
    const queryParams = new URLSearchParams();
    if (sortBy) {
        queryParams.append('sort_by', sortBy);
    }
    if (sortDir) {
        queryParams.append('sort_dir', sortDir);
    }
    if (page) {
        queryParams.append('page', page);
    }
    if (limit) {
        queryParams.append('limit', limit);
    }
    if (from) {
        queryParams.append('from', from);
    }
    if (to) {
        queryParams.append('to', to);
    }
    const response = await fetch(`/api/v1/payments?${queryParams.toString()}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchExpenses = async (sortBy, sortDir, page, limit, from, to) => {
    const queryParams = new URLSearchParams();
    if (sortBy) {
        queryParams.append('sort_by', sortBy);
    }
    if (sortDir) {
        queryParams.append('sort_dir', sortDir);
    }
    if (page) {
        queryParams.append('page', page);
    }
    if (limit) {
        queryParams.append('limit', limit);
    }
    if (from) {
        queryParams.append('from', from);
    }
    if (to) {
        queryParams.append('to', to);
    }
    const response = await fetch(`/api/v1/expenses?${queryParams.toString()}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createExpense = async (expenseData) => {
    const response = await fetch('/api/v1/expenses', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(expenseData)
    });
    return handleResponse(response);
};

export const deleteExpense = async (id) => {
    const response = await fetch(`/api/v1/expenses/${id}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const fetchExpenseCategories = async () => {
    const response = await fetch('/api/v1/expenses/categories', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    const data = await handleResponse(response);
    return data.data
};

export const createExpenseCategory = async (categoryData) => {
    const response = await fetch('/api/v1/expenses/categories', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(categoryData)
    });
    return handleResponse(response);
};

export const editExpenseCategory = async (id, categoryData) => {
    const response = await fetch(`/api/v1/expenses/categories/${id}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(categoryData)
    });
    return handleResponse(response);
}
   
   export const fetchProfitAndLossActual = async (from, to) => {
       const queryParams = new URLSearchParams();
       if (from) {
           queryParams.append('from', from);
       }
       if (to) {
           queryParams.append('to', to);
       }
       const response = await fetch(`/api/v1/reports/financial/profit-and-loss/actual?${queryParams.toString()}`, {
           headers: {
               'Authorization': `Bearer ${localStorage.getItem('token')}`
           }
       });
       return handleResponse(response);
   };
   
   export const fetchProfitAndLossExpected = async (from, to) => {
       const queryParams = new URLSearchParams();
       if (from) {
           queryParams.append('from', from);
       }
       if (to) {
           queryParams.append('to', to);
       }
       const response = await fetch(`/api/v1/reports/financial/profit-and-loss/expected?${queryParams.toString()}`, {
           headers: {
               'Authorization': `Bearer ${localStorage.getItem('token')}`
           }
       });
       return handleResponse(response);
   };
   
   export const fetchExpenseDetails = async (from, to) => {
       const queryParams = new URLSearchParams();
       if (from) {
           queryParams.append('from', from);
       }
       if (to) {
           queryParams.append('to', to);
       }
       const response = await fetch(`/api/v1/reports/financial/expenses/details?${queryParams.toString()}`, {
           headers: {
               'Authorization': `Bearer ${localStorage.getItem('token')}`
           }
       });
       return handleResponse(response);
   };
   
export const deleteExpenseCategory = async (id) => {
    const response = await fetch(`/api/v1/expenses/categories/${id}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    if (response.ok) {
        return;
    }
    return handleResponse(response);
};

export const forgotPassword = async (email) => {
    const response = await fetch('/api/v1/password/forgot', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
    });
    return handleResponse(response);
};
   
export const resetPassword = async (token, password, confirm_password) => {
    const response = await fetch('/api/v1/password/reset', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
           },
        body: JSON.stringify({ token, password, confirm_password }),
    });
    return handleResponse(response);
};

export const changePassword = async (oldPassword, newPassword, confirmPassword) => {
    const response = await fetch('/api/v1/password/change', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
            old_password: oldPassword,
            new_password: newPassword,
            confirm_password: confirmPassword
        })
    });
    return handleResponse(response);
};

export const fetchCalendar = async (year, month) => {
    const response = await fetch(`/api/v1/calendar?year=${year}&month=${month}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const fetchChats = async () => {
    const response = await fetch('/api/v1/chats', {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createChat = async (userId) => {
    const response = await fetch(`/api/v1/chats/users/${userId}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const sendMessage = async (chatId, text) => {
    const response = await fetch(`/api/v1/chats/${chatId}/messages`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ text })
    });
    return handleResponse(response);
};

export const getMessages = async (chatId, page, limit) => {
    const queryParams = new URLSearchParams();
    if (page) {
        queryParams.append('page', page);
    }
    if (limit) {
        queryParams.append('limit', limit);
    }
    const response = await fetch(`/api/v1/chats/${chatId}/messages?${queryParams.toString()}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const updateMessage = async (chatId, messageId, text) => {
    const response = await fetch(`/api/v1/chats/${chatId}/messages/${messageId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ text })
    });
    return handleResponse(response);
};

export const deleteMessage = async (chatId, messageId) => {
    const response = await fetch(`/api/v1/chats/${chatId}/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};

export const createGroupChat = async (name, member_ids) => {
    const response = await fetch('/api/v1/chats/group', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name, member_ids })
    });
    return handleResponse(response);
};

export const updateChat = async (chatId, data) => {
    const response = await fetch(`/api/v1/chats/${chatId}`,
        {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(data)
        });
    return handleResponse(response);
};

export const deleteChat = async (chatId) => {
    const response = await fetch(`/api/v1/chats/${chatId}`,
        {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
    return handleResponse(response);
};

export const fetchStudentStats = async (year, month) => {
    const queryParams = new URLSearchParams();
    queryParams.append('year', year);
    if (month) {
        queryParams.append('month', month);
    }
    const response = await fetch(`/api/v1/reports/stats/students?${queryParams.toString()}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
    return handleResponse(response);
};
